/* Linsoul recommended by: endless drift (eases to a stop under the mouse), arrows + progress line, publication logos jump to their first card, GSAP entrance. */
(() => {
  'use strict';
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const offscreen = (el) => {
    const r = el.getBoundingClientRect();
    return r.top > window.innerHeight || r.bottom < 0;
  };
  const loadGsap = (src) => (window.gsap ? Promise.resolve(window.gsap) : window.lsLoadGsap ? window.lsLoadGsap(src) : Promise.reject());

  const EASE = 0.45; /* seconds to reach ~63% of the target speed */

  function rail(root) {
    const list = root.querySelector('[data-lpr-rail]');
    const nav = root.querySelector('[data-lpr-nav]');
    const prev = root.querySelector('[data-lpr-prev]');
    const next = root.querySelector('[data-lpr-next]');
    const progress = root.querySelector('[data-lpr-progress]');
    const bar = root.querySelector('[data-lpr-bar]');
    if (!list) return;
    const cards = [...list.querySelectorAll('[data-lpr-card]')];
    const speed = parseFloat(root.dataset.lprSpeed) || 0;
    const drift = speed > 0 && !(window.Shopify && Shopify.designMode);
    const gap = () => parseFloat(getComputedStyle(list).columnGap) || 0;
    const step = () => (cards[0] ? cards[0].getBoundingClientRect().width + gap() : list.clientWidth);
    const behavior = () => (reduce() ? 'auto' : 'smooth');

    /* Clones after the last card so the loop has no visible seam. */
    let clones = [];
    let loopW = 0;
    const buildClones = () => {
      clones.forEach((c) => c.remove());
      clones = [];
      loopW = 0;
      if (!drift || cards.length < 2 || list.scrollWidth - list.clientWidth <= 2) return;
      let filled = 0;
      for (const card of cards) {
        if (filled > list.clientWidth + step()) break;
        const c = card.cloneNode(true);
        c.classList.remove('is-target');
        c.setAttribute('aria-hidden', 'true');
        c.dataset.lprClone = '';
        c.removeAttribute('data-shopify-editor-block');
        c.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        c.querySelectorAll('a,button').forEach((el) => el.setAttribute('tabindex', '-1'));
        list.appendChild(c);
        clones.push(c);
        filled += card.getBoundingClientRect().width + gap();
      }
      loopW = clones.length ? clones[0].offsetLeft - cards[0].offsetLeft : 0;
    };

    let frameQ = 0;
    const update = () => {
      frameQ = 0;
      const max = list.scrollWidth - list.clientWidth;
      const overflow = max > 2;
      if (nav) nav.hidden = !overflow;
      if (progress) progress.hidden = !overflow;
      if (!overflow) return;
      if (prev) prev.disabled = !loopW && list.scrollLeft <= 2;
      if (next) next.disabled = !loopW && list.scrollLeft >= max - 2;
      if (bar && progress) {
        const track = progress.clientWidth;
        const span = loopW || max;
        const thumb = Math.max(40, track * (list.clientWidth / (span + list.clientWidth)));
        const at = loopW ? (list.scrollLeft % loopW) / loopW : list.scrollLeft / max;
        bar.style.setProperty('--lpr-thumb', thumb + 'px');
        bar.style.setProperty('--lpr-x', ((track - thumb) * Math.min(1, at)) + 'px');
      }
    };
    const queue = () => { if (!frameQ) frameQ = requestAnimationFrame(update); };
    list.addEventListener('scroll', queue, { passive: true });

    /* Drift: constant speed, eases to 0 under the mouse / focus / touch / after arrows. */
    let pos = 0;
    let lastSet = -1;
    let v = 0;
    let last = 0;
    let raf = 0;
    let hover = false;
    let focus = false;
    let visible = false;
    let holdUntil = 0;
    const target = () => (hover || focus || performance.now() < holdUntil || document.hidden || reduce() ? 0 : speed);
    const hold = (ms) => { holdUntil = performance.now() + ms; v = 0; start(); };
    const frame = (now) => {
      raf = 0;
      const dt = Math.min((now - (last || now)) / 1000, 0.1);
      last = now;
      if (Math.abs(list.scrollLeft - lastSet) > 2) pos = list.scrollLeft;
      const t = target();
      v += (t - v) * (1 - Math.exp(-dt / EASE));
      if (t === 0 && v < 0.5) v = 0;
      if (v > 0 && loopW) {
        pos += v * dt;
        if (pos >= loopW) pos -= loopW;
        list.scrollLeft = pos;
        lastSet = list.scrollLeft;
      } else if (loopW && performance.now() >= holdUntil && list.scrollLeft >= loopW) {
        pos = list.scrollLeft - loopW;
        list.scrollLeft = pos;
        lastSet = list.scrollLeft;
      }
      if (visible && (v > 0 || t > 0 || performance.now() < holdUntil)) raf = requestAnimationFrame(frame);
      else last = 0;
    };
    const start = () => { if (drift && loopW && visible && !raf) raf = requestAnimationFrame(frame); };

    if (drift) {
      list.classList.add('is-drifting');
      list.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hover = true; });
      list.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { hover = false; start(); } });
      list.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) focus = true; });
      list.addEventListener('focusout', () => { focus = false; start(); });
      list.addEventListener('touchstart', () => hold(60000), { passive: true });
      list.addEventListener('touchend', () => hold(2500), { passive: true });
      list.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) hold(1500); }, { passive: true });
      document.addEventListener('visibilitychange', start);
      new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; start(); }).observe(list);
    }

    const go = (dir) => {
      hold(1200);
      if (dir < 0 && loopW && list.scrollLeft < step() - 2) {
        list.scrollLeft += loopW;
        pos = list.scrollLeft;
      }
      list.scrollBy({ left: dir * step(), behavior: behavior() });
    };
    prev && prev.addEventListener('click', () => go(-1));
    next && next.addEventListener('click', () => go(1));

    root.querySelectorAll('[data-lpr-jump]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const card = cards.find((c) => c.dataset.pub === btn.dataset.lprJump);
        if (!card) return;
        hold(2500);
        const pad = parseFloat(getComputedStyle(list).scrollPaddingLeft) || 0;
        list.scrollTo({ left: card.offsetLeft - pad, behavior: behavior() });
        cards.forEach((c) => c.classList.toggle('is-target', c === card));
        clearTimeout(card._lprT);
        card._lprT = setTimeout(() => card.classList.remove('is-target'), 1600);
      });
    });

    let width = 0;
    const resize = () => {
      if (list.clientWidth !== width) {
        width = list.clientWidth;
        buildClones();
      }
      update();
      start();
    };
    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(list);
    else window.addEventListener('resize', resize);
    resize();
  }

  function reveal(root) {
    if (!('IntersectionObserver' in window)) return;
    const near = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      near.disconnect();
      loadGsap(root.dataset.gsapSrc).then((gsap) => {
        gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
          if (!offscreen(root)) return;
          const head = root.querySelectorAll('[data-lpr-reveal]');
          const logos = root.querySelectorAll('.lpr-strip__logos li');
          const list = root.querySelector('[data-lpr-rail]');
          const box = list.getBoundingClientRect();
          const cards = [...list.querySelectorAll('[data-lpr-card]:not([data-lpr-clone])')].filter((c) => c.getBoundingClientRect().left < box.right);
          gsap.set(head, { autoAlpha: 0, y: 24 });
          gsap.set(logos, { autoAlpha: 0, y: 10 });
          gsap.set(cards, { autoAlpha: 0, x: 48 });
          const tl = gsap.timeline({ paused: true, defaults: { ease: 'expo.out', duration: 0.9 } });
          tl.to(head, { autoAlpha: 1, y: 0, stagger: 0.08, clearProps: 'transform,opacity,visibility' }, 0)
            .to(logos, { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.035, clearProps: 'transform,opacity,visibility' }, 0.15)
            .to(cards, { autoAlpha: 1, x: 0, duration: 1.1, stagger: 0.08, clearProps: 'transform,opacity,visibility' }, 0.2);
          const enter = new IntersectionObserver((entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            enter.disconnect();
            tl.play();
          }, { rootMargin: '0px 0px -15% 0px' });
          enter.observe(root);
          return () => { enter.disconnect(); tl.kill(); };
        });
      }).catch(() => {});
    }, { rootMargin: '600px 0px' });
    near.observe(root);
  }

  function init(root) {
    if (root.dataset.lprReady) return;
    root.dataset.lprReady = '1';
    rail(root);
    reveal(root);
  }

  const run = () => document.querySelectorAll('[data-lpr]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
